import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Instagram, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "304-312-9317";
const EMAIL = "coachjohnsonrecruiting9@gmail.com";
const INSTAGRAM = "https://instagram.com/jjspeedfactory";

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState("");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (hp) { setDone(true); return; }
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you! Your message has been received.");
      setForm(EMPTY);
    } catch {
      toast.error("We couldn't send that right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-white outline-none py-3 text-white placeholder-white/30 transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
              Connect
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
              Let's Build Your Performance
            </h2>
            <p className="mt-6 text-white/65 text-lg max-w-md">
              Ready to become faster, more explosive, and more athletic? Let's
              build your performance together.
            </p>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-location">
                <MapPin size={20} className="text-white/70 mt-1 shrink-0" />
                <div>
                  <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">
                    Mobile Training
                  </p>
                  <p className="font-display font-bold uppercase tracking-tight text-xl">Northeast Ohio</p>
                  <p className="text-white/50 text-sm mt-1 max-w-sm">
                    Mobile, off-site training across Northeast Ohio — Canton, Akron,
                    Wooster, New Philadelphia, Alliance & surrounding communities.
                  </p>
                </div>
              </div>

              <a href={`tel:${PHONE.replace(/[^0-9]/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <Phone size={20} className="text-white/70 shrink-0" />
                <span className="text-white/80 group-hover:text-white transition-colors text-lg">{PHONE}</span>
              </a>

              <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <Mail size={20} className="text-white/70 shrink-0" />
                <span className="text-white/80 group-hover:text-white transition-colors text-lg break-all">{EMAIL}</span>
              </a>

              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 group">
                <Instagram size={20} className="text-white/70 shrink-0" />
                <span className="text-white/80 group-hover:text-white transition-colors text-lg">@jjspeedfactory</span>
              </a>
            </div>

            <button
              data-testid="contact-book-btn"
              onClick={() => scrollTo("#assessment")}
              className="mt-10 inline-flex items-center justify-center gap-2 bg-white text-black uppercase tracking-[0.2em] font-bold text-sm px-8 py-5 hover:bg-white/85 transition-colors duration-300"
            >
              Book Free Evaluation <ArrowUpRight size={18} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="bg-[#0a0a0a] border border-white/10 p-8 md:p-10"
            data-testid="contact-form-wrap"
          >
            {done ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="contact-success">
                <CheckCircle2 size={52} className="text-white mb-6" />
                <h3 className="font-display font-black uppercase tracking-tight text-3xl mb-3">Message Sent</h3>
                <p className="text-white/60 max-w-md">
                  Thank you! Your information has been received. Coach Johnson will
                  review your submission and personally reach out to you soon.
                </p>
                <button
                  data-testid="contact-reset-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 border border-white/20 text-white uppercase tracking-[0.2em] font-bold text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <input type="text" name="company" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                <p className="font-mono-accent text-xs tracking-[0.25em] uppercase text-white/40">Send a Message</p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Name *</label>
                    <input data-testid="contact-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Phone</label>
                    <input data-testid="contact-form-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(555) 000-0000" />
                  </div>
                </div>
                <div>
                  <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Email *</label>
                  <input data-testid="contact-form-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                </div>
                <div>
                  <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Subject</label>
                  <input data-testid="contact-subject" value={form.subject} onChange={update("subject")} className={inputCls} placeholder="How can we help?" />
                </div>
                <div>
                  <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Message *</label>
                  <textarea data-testid="contact-message" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us what you're looking for..." />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-black uppercase tracking-[0.2em] font-bold py-4 hover:bg-white/85 transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

